import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ChevronLeft, Send, CheckCircle2, Info } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { QUESTIONNAIRE, QUESTIONNAIRE_I18N } from "@/i18n/questionnaire";
import { api, formatApiErrorDetail } from "@/lib/api";

const DRAFT_KEY = "kuryarti.brief.draft";

export default function NewProject() {
  const { lang } = useLang();
  const tr = QUESTIONNAIRE_I18N[lang] || QUESTIONNAIRE_I18N.en;
  const { user } = useAuth();
  const navigate = useNavigate();

  const totalQuestions = useMemo(
    () => QUESTIONNAIRE.sections.reduce((a, s) => a + s.ids.length, 0),
    []
  );

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.answers || {};
      }
    } catch {}
    return {};
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const debounceRef = useRef(null);

  const totalSteps = QUESTIONNAIRE.sections.length + 1; // +1 for intro
  const filledCount = Object.values(answers).filter((v) => v && v.trim().length > 0).length;
  const pct = Math.round((filledCount / totalQuestions) * 100);

  const setAnswer = (qid, v) => {
    setAnswers((a) => {
      const next = { ...a, [qid]: v };
      // Debounced localStorage save
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        try {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({ answers: next, saved_at: Date.now() }));
          setSavedAt(new Date());
        } catch {}
      }, 500);
      return next;
    });
  };

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/requests", { title: answers.q1 || "Untitled project", answers });
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      setDone(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="newproject-success"
            className="bg-white border border-[#00d4ff] p-10 lg:p-14"
          >
            <div className="w-12 h-12 border border-[#00d4ff] flex items-center justify-center mb-8">
              <CheckCircle2 size={20} className="text-[#00d4ff]" />
            </div>
            <h1 className="font-mono font-bold tracking-tighter text-3xl text-[#0a0a0a]">
              {tr.intro.success_title}
            </h1>
            <p className="mt-4 font-sans text-base text-[#4a4a4a] max-w-xl">
              {tr.intro.success_text}
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="newproject-goto-dashboard"
              className="mt-10 inline-flex items-center gap-3 px-6 py-4 bg-[#0a0a0a] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors"
            >
              {tr.intro.goto_dashboard}
              <ChevronRight size={14} />
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5] pt-24 pb-24">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-2">
              · {user?.email}
            </div>
            <h1 className="font-mono font-bold tracking-tighter text-3xl sm:text-4xl text-[#0a0a0a]">
              {tr.intro.title}
            </h1>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] text-right">
            <div>{filledCount} / {totalQuestions}</div>
            <div className="mt-1">{pct}% complete</div>
            {savedAt && (
              <div className="mt-1 text-[#00d4ff]" data-testid="brief-autosave-indicator">
                ✓ auto-saved
              </div>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-[#eeeeee] mb-10 relative">
          <motion.div
            className="absolute top-0 left-0 h-full bg-[#ff006e]"
            initial={{ width: 0 }}
            animate={{ width: `${(step / (totalSteps - 1)) * 100}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>

        <AnimatePresence mode="wait">
          {step === 0 ? (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="bg-white border border-[#0a0a0a] p-8 lg:p-12"
            >
              <p className="font-sans text-base text-[#4a4a4a] leading-relaxed">{tr.intro.lead}</p>
              <p className="mt-4 font-sans text-sm text-[#4a4a4a] leading-relaxed">{tr.intro.escrow}</p>

              <div className="mt-10">
                <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#0a0a0a] mb-4">
                  {tr.intro.workflow_title}
                </div>
                <ol className="space-y-3">
                  {tr.intro.workflow.map((w, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="font-mono text-xs text-[#ff006e] w-6">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-sans text-sm text-[#4a4a4a] leading-relaxed">{w}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-6 font-sans text-sm text-[#4a4a4a] leading-relaxed">{tr.intro.approvals}</p>
              </div>

              <div className="mt-10 p-5 bg-[#f5f5f5] border-l-2 border-[#00d4ff]">
                <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#0a0a0a] mb-2">
                  {tr.intro.payment_title}
                </div>
                <p className="font-sans text-sm text-[#4a4a4a]">{tr.intro.payment}</p>
              </div>

              <div className="mt-10 flex justify-end">
                <button
                  onClick={() => setStep(1)}
                  data-testid="newproject-start-btn"
                  className="inline-flex items-center gap-3 px-6 py-4 bg-[#0a0a0a] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors"
                >
                  {tr.intro.cta}
                  <ChevronRight size={14} />
                </button>
              </div>
            </motion.div>
          ) : (
            (() => {
              const section = QUESTIONNAIRE.sections[step - 1];
              const sectionTitle = tr.sections[section.id];
              const isLast = step === totalSteps - 1;

              return (
                <motion.div
                  key={`step-${step}`}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.35 }}
                  className="bg-white border border-[#0a0a0a]"
                >
                  <div className="px-8 lg:px-12 py-6 border-b border-[#0a0a0a] flex items-center justify-between">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
                        Section {step} / {QUESTIONNAIRE.sections.length}
                      </div>
                      <h2 className="mt-1 font-mono font-bold tracking-tight text-xl text-[#0a0a0a]">
                        {sectionTitle}
                      </h2>
                    </div>
                  </div>

                  <div className="p-8 lg:p-12 space-y-8">
                    {section.ids.map((qid, i) => {
                      const q = tr.q[qid];
                      const value = answers[qid] || "";
                      const isLong = ["q12", "q13", "q20", "q22"].includes(qid);
                      return (
                        <div key={qid}>
                          <div className="flex items-start gap-3 mb-3">
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#ff006e] mt-0.5">
                              {qid.replace("q_extra", "Q+").replace("q", "Q")}
                            </span>
                            <div className="flex-1">
                              <label
                                htmlFor={`q-${qid}`}
                                className="block font-sans text-[15px] font-medium text-[#0a0a0a] leading-snug"
                              >
                                {q.label}
                              </label>
                              {q.hint && (
                                <div className="mt-1 flex gap-2 font-sans text-xs text-[#8a8a8a] leading-relaxed">
                                  <Info size={12} className="mt-0.5 flex-shrink-0" />
                                  <span>{q.hint}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {isLong ? (
                            <textarea
                              id={`q-${qid}`}
                              data-testid={`q-${qid}`}
                              value={value}
                              onChange={(e) => setAnswer(qid, e.target.value)}
                              rows={4}
                              placeholder="–"
                              className="w-full px-3 py-2.5 border border-[#0a0a0a] bg-white text-[#0a0a0a] font-sans text-sm focus:outline-none focus:border-[#ff006e] transition-colors resize-y"
                            />
                          ) : (
                            <input
                              id={`q-${qid}`}
                              data-testid={`q-${qid}`}
                              value={value}
                              onChange={(e) => setAnswer(qid, e.target.value)}
                              placeholder="–"
                              className="w-full px-3 py-2.5 border border-[#0a0a0a] bg-white text-[#0a0a0a] font-sans text-sm focus:outline-none focus:border-[#ff006e] transition-colors"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {err && (
                    <div className="px-8 lg:px-12 pb-4 font-mono text-xs text-[#ff006e]">{err}</div>
                  )}

                  <div className="px-8 lg:px-12 py-6 border-t border-[#0a0a0a] flex items-center justify-between bg-[#fafafa]">
                    <button
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      data-testid="newproject-prev"
                      className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#0a0a0a] hover:text-[#ff006e]"
                    >
                      <ChevronLeft size={14} />
                      Back
                    </button>
                    {isLast ? (
                      <button
                        onClick={submit}
                        disabled={submitting}
                        data-testid="newproject-submit"
                        className="inline-flex items-center gap-3 px-6 py-3 bg-[#0a0a0a] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors disabled:opacity-50"
                      >
                        {submitting ? "…" : tr.intro.submit}
                        <Send size={14} />
                      </button>
                    ) : (
                      <button
                        onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
                        data-testid="newproject-next"
                        className="inline-flex items-center gap-3 px-6 py-3 bg-[#0a0a0a] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors"
                      >
                        Next
                        <ChevronRight size={14} />
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })()
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}